import {
  ACTIVITIES_ANSWER_TEXT,
  SUGGESTED_ANSWER_EXAMPLE,
} from 'global/constants';
import type { RichTextAnswerFragment } from '../../../present/activity-answers.graphql.generated';
import type { SuggestedAnswerFragment } from '../../suggested-answers/suggested-answers.graphql.generated';
import { AnswerInput } from '../batch-suggested-answers.context';
import type { Pluck } from 'lib/gql/pluck';

export type AnswerMap = {
  [key: string]: Pluck<SuggestedAnswerFragment, 'suggestedAnswers'> | null;
};

export function getTableAnswerMap(
  answers: Pluck<SuggestedAnswerFragment, 'suggestedAnswers'>[] | null
): AnswerMap {
  return (
    answers?.reduce<AnswerMap>((acc, item) => {
      if (item.answerReferences?.id === null) {
        acc[
          getTableAnswerKey(
            item.answerReferences?.row,
            item.answerReferences?.col
          )
        ] = {
          ...item,
          answer: {
            __typename: 'RichtextAnswer',
            answerType: ACTIVITIES_ANSWER_TEXT,
            richtext: (item.answer as RichTextAnswerFragment)?.richtext,
          },
        };
      }
      return acc;
    }, {}) || {}
  );
}

export function getTableAnswerKey(
  row?: number | null,
  col?: number | null
): string {
  return `${row}:${col}`;
}

function getTableAnswerKeyValues(key: string) {
  return key.split(':').map(Number);
}

export function updateAnswerMapAfterRowInsert(
  answerMap: AnswerMap,
  insertedRowIndex: number
) {
  const updatedData: AnswerMap = {};
  let hasChanged: boolean = false;

  for (let key in answerMap) {
    const [rowIndex, cellIndex] = getTableAnswerKeyValues(key);

    if (rowIndex > insertedRowIndex) {
      const newKey = getTableAnswerKey(rowIndex + 1, cellIndex);
      updatedData[newKey] = answerMap[key];
      const item = updatedData[newKey];
      if (item) {
        item.answerReferences = {
          __typename: 'ReferenceProperties',
          row: rowIndex + 1,
          col: cellIndex,
          id: null,
        };
      }
      hasChanged = true;
    } else {
      updatedData[key] = answerMap[key];
    }
  }

  return hasChanged ? updatedData : undefined;
}

export function updateAnswerMapAfterRowDelete(
  answerMap: AnswerMap,
  deletedRowIndex: number
) {
  const updatedData: AnswerMap = {};
  let hasChanged: boolean = false;

  for (let key in answerMap) {
    const [rowIndex, cellIndex] = getTableAnswerKeyValues(key);

    if (rowIndex === deletedRowIndex) {
      hasChanged = true;
      continue;
    }

    if (rowIndex > deletedRowIndex) {
      const newKey = getTableAnswerKey(rowIndex - 1, cellIndex);
      updatedData[newKey] = answerMap[key];
      const item = updatedData[newKey];
      if (item) {
        item.answerReferences = {
          __typename: 'ReferenceProperties',
          row: rowIndex - 1,
          col: cellIndex,
          id: null,
        };
        hasChanged = true;
      }
    } else {
      updatedData[key] = answerMap[key];
    }
  }

  return hasChanged ? updatedData : undefined;
}

export function updateAnswerMapAfterColumnInsert(
  answerMap: AnswerMap,
  insertedColumnIndex: number
) {
  const updatedData: AnswerMap = {};
  let hasChanged: boolean = false;

  for (let key in answerMap) {
    const [rowIndex, cellIndex] = getTableAnswerKeyValues(key);

    if (cellIndex > insertedColumnIndex) {
      const newKey = getTableAnswerKey(rowIndex, cellIndex + 1);
      updatedData[newKey] = answerMap[key];
      const item = updatedData[newKey];
      if (item) {
        item.answerReferences = {
          __typename: 'ReferenceProperties',
          row: rowIndex,
          col: cellIndex + 1,
          id: null,
        };
        hasChanged = true;
      }
    } else {
      updatedData[key] = answerMap[key];
    }
  }

  return hasChanged ? updatedData : undefined;
}

export function updateAnswerMapAfterColumnDelete(
  answerMap: AnswerMap,
  deletedColumnIndex: number
) {
  const updatedData: AnswerMap = {};
  let hasChanged: boolean = false;

  for (let key in answerMap) {
    const [rowIndex, cellIndex] = getTableAnswerKeyValues(key);

    if (cellIndex === deletedColumnIndex) {
      hasChanged = true;
      continue;
    }

    if (cellIndex > deletedColumnIndex) {
      const newKey = getTableAnswerKey(rowIndex, cellIndex - 1);
      updatedData[newKey] = answerMap[key];
      const item = updatedData[newKey];
      if (item) {
        item.answerReferences = {
          __typename: 'ReferenceProperties',
          row: rowIndex,
          col: cellIndex - 1,
          id: null,
        };
        hasChanged = true;
      }
    } else {
      updatedData[key] = answerMap[key];
    }
  }

  return hasChanged ? updatedData : undefined;
}

export function getSuggestedAnswersInput(
  answersMap: AnswerMap,
  suggestedAnswerProperties: SuggestedAnswerFragment | null
) {
  const answers: AnswerInput = {
    __typename: 'ComponentSuggestedAnswersProperties',
    suggestedAnswerType:
      suggestedAnswerProperties?.suggestedAnswerType ||
      SUGGESTED_ANSWER_EXAMPLE,
    suggestedAnswersScore:
      suggestedAnswerProperties?.suggestedAnswersScore || 1,
    suggestedAnswers:
      suggestedAnswerProperties?.suggestedAnswers?.filter(
        f => f.answerReferences?.id
      ) || [],
    markingGuides: suggestedAnswerProperties?.markingGuides || null,
  };

  for (const item in answersMap) {
    const answer = answersMap[item];
    if (answer && answers.suggestedAnswers) {
      answers.suggestedAnswers.push(answer);
    }
  }

  return answers;
}
